import type { ApiHttpClient } from "../interfaces/api-http-client";
import type {
  Folder,
  FolderView,
  FolderListResponse,
  FolderListQueryParams,
  ReanalyzeFolderParams,
  ApiResult,
  CreateFolderData,
  CreateFolderResult,
  UpdateFolderData,
  UpdateFolderResult,
  CloneFolderData,
  CloneFolderResult,
  CreateFolderViewData,
} from "./types";

export function createFolderApi(http: ApiHttpClient) {
  return {
    /** Fetch paginated folder list. */
    getFolderList(params: FolderListQueryParams) {
      const { page, pageSize, sortBy, sortDir, query, sourceType, from, to, assignTo } = params;

      const searchParams = new URLSearchParams();
      searchParams.set("page", String(page));
      searchParams.set("pageSize", String(pageSize));
      searchParams.set("sortBy", `${sortBy}:${sortDir}`);
      searchParams.set("filterName", query);
      if (sourceType) searchParams.set("sourceType", sourceType);
      if (from) searchParams.set("from", from);
      if (to) searchParams.set("to", to);
      if (assignTo.length > 0) searchParams.set("assignTo", assignTo.join(","));

      return http.get<FolderListResponse>(`/folder?${searchParams.toString()}`);
    },

    /** Fetch a single folder by ID. */
    getFolderDetail(id: string) {
      return http.get<Folder>(`/folder/${id}`);
    },

    /** Fetch saved views for a folder. Server returns FolderView[] from the folderviews collection. */
    getFolderViews(folderId: string) {
      return http.get<FolderView[]>(`/folder/${folderId}/views`);
    },

    /** Create a new folder. */
    createFolder(data: CreateFolderData) {
      return http.post<CreateFolderResult>("/folder", data);
    },

    /** Update an existing folder. */
    updateFolder(folderId: string, data: UpdateFolderData) {
      return http.put<UpdateFolderResult>(`/folder/${folderId}`, data);
    },

    /** Delete a folder. */
    deleteFolder(folderId: string) {
      return http.delete<ApiResult>(`/folder/${folderId}`);
    },

    /** Clone a folder. */
    cloneFolder(data: CloneFolderData) {
      return http.post<CloneFolderResult>("/folder/clone", data);
    },

    /** Re-analyze all media in a folder. Angular: FolderService → POST /folder/reanalyze. */
    reanalyzeFolder(params: ReanalyzeFolderParams) {
      return http.post<{ message: string }>("/folder/reanalyze", params);
    },

    /** Create a new saved view for a folder. */
    createFolderView(folderId: string, data: CreateFolderViewData) {
      return http.post<FolderView[]>(`/folder/${encodeURIComponent(folderId)}/views`, data);
    },

    /** Update an existing saved view. */
    updateFolderView(folderId: string, viewId: string, data: CreateFolderViewData) {
      return http.put<FolderView[]>(
        `/folder/${encodeURIComponent(folderId)}/views/${encodeURIComponent(viewId)}`,
        data,
      );
    },

    /** Delete a saved view. */
    deleteFolderView(folderId: string, viewId: string) {
      return http.delete<ApiResult>(
        `/folder/${encodeURIComponent(folderId)}/views/${encodeURIComponent(viewId)}`,
      );
    },

    /** Update drag-and-drop sort order. Angular: FolderService.updateShowOrder() → PUT /folder/showOrder/:folderId */
    updateShowOrder(folderId: string, prevIndex: number, currentIndex: number) {
      return http.put<ApiResult>(`/folder/showOrder/${folderId}`, { prevIndex, currentIndex });
    },

    /**
     * Configure field-level AI automation for a folder.
     * Angular: FolderService.fieldsAutomation() → PUT /folder/automation/:folderId
     *
     * Server Joi `folderFieldsAutomation` only accepts `{ fields: [{ fieldId }] }` (max 10).
     * Any additional keys (e.g. `promptText`) are rejected with 400.
     */
    setupFolderAutomation(folderId: string, fields: Array<{ fieldId: string }>) {
      return http.put<ApiResult>(`/folder/automation/${encodeURIComponent(folderId)}`, { fields });
    },
  };
}
